// redux/automations/selectors.ts

import { RootState } from "../store";
import { createSelector } from "@reduxjs/toolkit";

// Company selectors
export const selectCompanies = (state: RootState) =>
  state.automations.companies;

export const selectActiveCompany = (state: RootState) =>
  state.automations.activeCompany;

export const selectCompanyOptions = createSelector(
  [selectCompanies],
  (companies) =>
    companies.map((company) => ({
      value: `${company.id}|${company.type}`,
      label: `${company.name} - ${company.type}`,
      companyId: company.id,
      companyType: company.type,
      companyName: company.name,
    }))
);

// Loading selectors
export const selectIsLoading = (state: RootState) => state.automations.loading;

export const selectIsExecuting = (state: RootState) =>
  state.automations.executing;

// Node configuration selectors
export const selectNodeConfigs = (state: RootState) =>
  state.automations.nodeConfigs;

export const selectTriggerNodes = (state: RootState) =>
  state.automations.nodeConfigs.triggers;

export const selectConditionNodes = (state: RootState) =>
  state.automations.nodeConfigs.conditions;

export const selectActionNodes = (state: RootState) =>
  state.automations.nodeConfigs.actions;

export const selectValidationErrors = (state: RootState) =>
  state.automations.validationErrors;

export const selectNodeDynamicDataMap = (state: RootState) =>
  state.automations.nodeDynamicDataMap;

export const selectNodeDynamicData = (nodeKey: string) => (state: RootState) =>
  state.automations.nodeDynamicDataMap[nodeKey];

// Workflow selectors
export const selectWorkflows = (state: RootState) =>
  state.automations.workflows;

export const selectCurrentWorkflow = (state: RootState) =>
  state.automations.currentWorkflow;

export const selectWorkflowSettings = (state: RootState) =>
  state.automations.workflowSettings;

export const selectWorkflowPagination = (state: RootState) =>
  state.automations.workflowPagination;

export const selectActiveWorkflows = createSelector(
  [selectWorkflows],
  (workflows) => workflows.filter((w) => w.is_active)
);

export const selectInactiveWorkflows = createSelector(
  [selectWorkflows],
  (workflows) => workflows.filter((w) => !w.is_active)
);

export const selectWorkflowById = (workflowId: number) =>
  createSelector([selectWorkflows], (workflows) =>
    workflows.find((w) => w.id === workflowId)
  );

// Execution selectors
export const selectExecutionHistory = (state: RootState) =>
  state.automations.executionHistory;

export const selectCurrentExecution = (state: RootState) =>
  state.automations.currentExecution;

export const selectSuccessfulExecutions = createSelector(
  [selectExecutionHistory],
  (history) => history.filter((e) => e.status === "COMPLETED")
);

export const selectFailedExecutions = createSelector(
  [selectExecutionHistory],
  (history) => history.filter((e) => e.status === "FAILED")
);

export const selectRunningExecutions = createSelector(
  [selectExecutionHistory],
  (history) => history.filter((e) => e.status === "RUNNING")
);

// Version history selectors
export const selectVersionHistory = (state: RootState) =>
  state.automations.versionHistory;

export const selectLatestVersion = createSelector(
  [selectVersionHistory],
  (history) => (history.length > 0 ? history[0] : null)
);

// Validation selectors
export const selectWorkflowValidation = (state: RootState) =>
  state.automations.workflowValidation;

export const selectIsWorkflowValid = createSelector(
  [selectWorkflowValidation],
  (validation) => validation?.valid === true
);

export const selectValidationErrorCount = createSelector(
  [selectWorkflowValidation],
  (validation) => validation?.errors?.length || 0
);

export const selectValidationWarningCount = createSelector(
  [selectWorkflowValidation],
  (validation) => validation?.warnings?.length || 0
);
